import React, { useRef } from "react";
import clsx from "clsx";
import { useTranslation } from "lib/i18n/use-translation";
import { ReactComponent as CrossIcon } from "Assets/icon/cross-round.svg";
import { ReactComponent as ChevronIcon } from "Assets/icon/icon-chevron.svg";
import SkyBackground from "components/sky/SkyBackground";
import styles from "./ReportShell.module.scss";

export interface ReportShellProgress {
  /** Which screen the learner is on, counting from 1. */
  current: number;
  /** How many screens the flow holds for this learner. */
  total: number;
  /** How the position reads aloud, e.g. "Step 3 of 6". */
  label: string;
}

export interface ReportStepScore {
  /** The learner's score. */
  value: number | string;
  /** What it is out of. The designs show 100 throughout. */
  outOf?: number | string;
  /** What the readout is called. Supplied by the caller rather than hardcoded. */
  label: string;
}

export interface ReportShellProps {
  children: React.ReactNode;
  /** The report's sky. Off leaves the shell's own flat backdrop. */
  showSky?: boolean;
  /**
   * Leaves the sky at full brightness, without the contrast tint. The designs
   * light the wait, the cover and the score reveal this way, and darken the
   * sky from the first screen with content to read. The tint fades between
   * the two rather than switching.
   */
  brightSky?: boolean;
  /** Omitted on screens that cannot be dismissed, such as the wait. */
  onClose?: () => void;
  /**
   * Where the learner is in the flow. Omitted on screens outside it: the
   * designs pip the report's screens but not its cover.
   */
  progress?: ReportShellProgress;
  /**
   * The readout at the foot of the frame. Omitted on screens without one,
   * which leaves that corner empty without moving anything else.
   */
  score?: ReportStepScore;
  onBack?: () => void;
  /** Accessible name for the back control. Required when onBack is set. */
  backLabel?: string;
  onForward?: () => void;
  /** Accessible name for the forward control. Required when onForward is set. */
  forwardLabel?: string;
  /**
   * The controls are arriving on a step that was swapped in without the
   * report's fade, so they fade in on their own rather than appearing at
   * full strength beside a score that is still moving.
   */
  controlsArriving?: boolean;
  /**
   * The parts of the foot row the next step does not have, while the stage
   * fades out on its way there. They fade with it instead of holding at full
   * strength and then vanishing in one frame. Parts the next step keeps are
   * left out, so they hold still.
   */
  departing?: ReportShellDeparting;
  /**
   * Drawn over the frame, above the step indicator, which a step's own
   * content cannot paint over. Positioned by whatever is passed.
   */
  overlay?: React.ReactNode;
}

export interface ReportShellDeparting {
  score?: boolean;
  controls?: boolean;
}

const ScoreReadout: React.FC<{
  score: ReportStepScore;
  departing: boolean;
}> = ({ score, departing }) => {
  const valueKey = `${score.value}/${score.outOf ?? 100}`;
  // The readout fades in as a whole when it first shows, so the value inside
  // fades in only once it has changed. Fading both on arrival held the
  // number back behind its own label.
  const arrivedWith = useRef(valueKey);
  const changed = useRef(false);
  if (valueKey !== arrivedWith.current) changed.current = true;

  return (
    <div
      className={styles.score}
      data-departing={departing}
      data-testid="report-step-score"
    >
      <p className={styles["score-label"]}>{score.label}</p>
      {/*
        Three spans rather than one string: the design separates the score
        from what it is out of by colour at the same size, and sets the slash
        smaller than both. Reading order is unchanged, so the slash stays
        exposed to assistive tech as it was when it led "/100". Keyed on the
        value so a changed number replaces the old one and fades in.
      */}
      <p
        key={valueKey}
        className={styles["score-value"]}
        data-changed={changed.current}
      >
        <span className={styles["score-number"]}>{score.value}</span>
        <span className={styles["score-separator"]}>/</span>
        <span className={styles["score-out-of"]}>{score.outOf ?? 100}</span>
      </p>
    </div>
  );
};

/**
 * The frame every report screen sits in: a cream page with the content
 * inset and rounded, and the sky behind it.
 *
 * It exists as one component because the wait screen and the report
 * screens must share it exactly. The wait leads straight into the report,
 * so any difference in inset or radius reads as the page jumping.
 *
 * The foot row, the score readout and the back and forward controls, lives
 * here rather than in each step, and outside `children`, so it stays put
 * while the step content fades from one screen to the next. Inside the step
 * it faded with it, and the whole frame blinking read as a page reload
 * rather than one flow.
 */
const ReportShell: React.FC<ReportShellProps> = ({
  children,
  showSky = false,
  brightSky = false,
  onClose,
  progress,
  score,
  onBack,
  backLabel,
  onForward,
  forwardLabel,
  controlsArriving = false,
  departing,
  overlay,
}) => {
  const { t } = useTranslation(["assessment"]);

  return (
    <div className={styles.page} data-testid="report-shell">
      <div className={styles.frame}>
        {showSky && (
          <>
            <SkyBackground />
            {/*
              The sky is bright enough in places to take the white copy down
              to a few points of contrast, so a flat tint sits over it and
              under everything the learner reads. One layer in the shell, so
              no step can be left off it by accident: a bright step asks for
              it to be lifted. Kept mounted and faded, so the sky darkens
              into the report rather than switching.
            */}
            <div
              className={styles.scrim}
              data-testid="report-scrim"
              style={{ opacity: brightSky ? 0 : 1 }}
            />
          </>
        )}

        {progress && progress.total > 0 && (
          <div
            className={styles.progress}
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={progress.total}
            aria-valuenow={progress.current}
            aria-label={progress.label}
            data-testid="report-progress"
          >
            {/*
              The pips are drawn, not counted from a constant: the report
              leaves out any screen the learner has no data for, so a fixed
              six would tell some of them the flow is longer than it is.
            */}
            {Array.from({ length: progress.total }, (_, index) => (
              <span
                key={index}
                className={clsx(
                  styles.pip,
                  index + 1 === progress.current && styles["pip-current"]
                )}
                data-testid="report-pip"
              />
            ))}
          </div>
        )}

        <div className={styles.content}>{children}</div>

        {overlay}

        {score && (
          <ScoreReadout score={score} departing={Boolean(departing?.score)} />
        )}

        {(onBack || onForward) && (
          <div
            className={styles.controls}
            data-arriving={controlsArriving}
            data-departing={Boolean(departing?.controls)}
            data-testid="report-controls"
          >
            {onBack && (
              <button
                type="button"
                className={clsx(styles.control, styles.back)}
                onClick={onBack}
                aria-label={backLabel}
                data-testid="report-step-back"
              >
                <ChevronIcon className={styles.chevron} aria-hidden="true" />
              </button>
            )}

            {onForward && (
              <button
                type="button"
                className={clsx(styles.control, styles.forward)}
                onClick={onForward}
                aria-label={forwardLabel}
                data-testid="report-step-forward"
              >
                <ChevronIcon className={styles.chevron} aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        {onClose && (
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t("assessment:report.close")}
            data-testid="report-close"
          >
            <CrossIcon className={styles["close-glyph"]} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ReportShell;
